import { ChangeDetectionStrategy, Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DecimalPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import {
  AlertService,
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { VipLevelConfig, VipLevelUpClaim } from '@gains-com/shared';
import { GoldCoinsIconComponent } from '../../../../components/coins/gold-coins-icon.component.js';
import { SweepsCoinsIconComponent } from '../../../../components/coins/sweeps-coins-icon.component.js';
import { VipViewService } from '../../../../core/view-states/vip-view.service.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { vipBadgeUrl } from '../../vip-assets.js';
import { accountTimeoutMessage, accountTimeoutRejectionMessage } from '../../../../utils/timeout.js';

/** A level-up claim paired with its resolved tier config (config null when unconfigured). */
export interface LevelUpReward {
  claim: VipLevelUpClaim;
  config: VipLevelConfig | null;
}

export interface LevelUpRewardsListDialogData {
  /** All unclaimed level-up rewards, in the order the API returns them (created_at asc). */
  rewards: LevelUpReward[];
}

/**
 * Level-up rewards list dialog. Web mirror of the native
 * `UnclaimedRewardsSheet`: when the user has more than one unclaimed level-up
 * reward, the /vip pill opens this list — one row per reward (tier badge, the
 * SC + GC earned, a per-row Claim button) — instead of the single-reward dialog.
 * Claims one reward at a time (the other rows disable while one is in flight);
 * closes with `true` once any reward was claimed so the parent can refresh.
 *
 * Opened via `DialogService` (never raw CDK `Dialog`) — see the
 * `web-dialog-pattern` skill.
 */
@Component({
  selector: 'app-level-up-rewards-list-dialog',
  templateUrl: './level-up-rewards-list-dialog.component.html',
  styleUrl: './level-up-rewards-list-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    CardContentComponent,
    MatButton,
    DecimalPipe,
    ButtonLoadingDirective,
    IconComponent,
    GoldCoinsIconComponent,
    SweepsCoinsIconComponent,
    CardHeaderComponent,
    MatTooltip,
  ],
})
export class LevelUpRewardsListDialogComponent {
  private readonly _dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private readonly _data = inject<LevelUpRewardsListDialogData>(DIALOG_DATA);
  private readonly _vipView = inject(VipViewService);
  private readonly _userView = inject(UserViewService);
  private readonly _alert = inject(AlertService);
  private readonly _logger = inject(LOGGER_FACTORY).create('LevelUpRewardsListDialogComponent');
  private readonly _locale = inject(LOCALE_ID);

  /** Live list — each reward is dropped as its claim succeeds. */
  readonly rewards = signal<LevelUpReward[]>(this._data.rewards);
  /** vip_level_up_claim_id currently being claimed, or null — disables the other rows. */
  readonly claimingId = signal<string | null>(null);
  /** True once any reward was claimed, so the parent refreshes on close. */
  private _claimedAny = false;

  private readonly _user = toSignal(this._userView.state$);
  /** Active responsible-gaming Timeout message — disables the per-row Claim CTAs. */
  readonly timeout = computed(() => accountTimeoutMessage(this._user()?.timeout_until, this._locale));

  /** Tier display name; falls back to the claim's raw level identity. */
  title(reward: LevelUpReward): string {
    return reward.config?.title ?? reward.claim.level_identity;
  }

  /** Tier badge — per-tier `logo.webp` when keyed, else the stored `logo_url`. */
  badgeUrl(reward: LevelUpReward): string | null {
    return reward.config ? vipBadgeUrl(reward.config) : null;
  }

  async claim(reward: LevelUpReward): Promise<void> {
    if (this.claimingId() || this.timeout()) {
      return;
    }

    const id = reward.claim.vip_level_up_claim_id;
    this.claimingId.set(id);

    try {
      await this._vipView.claimLevelReward(id);
      this._claimedAny = true;
      this._alert.notifySuccess('Reward Claimed', 'Successfully claimed your VIP reward.');

      const remaining = this.rewards().filter((r) => r.claim.vip_level_up_claim_id !== id);
      this.rewards.set(remaining);
      if (!remaining.length) {
        this._dialogRef.close(true);
      }
    } catch (err: unknown) {
      const code = (err as { error?: { code?: string } })?.error?.code;
      if (code === 'ACCOUNT_TIMEOUT') {
        this._alert.notifyDanger(
          'Account Timeout',
          accountTimeoutRejectionMessage(this._user()?.timeout_until, this._locale),
        );
        await this._userView.syncTimeout();
      } else {
        const message =
          code === 'VIP_LEVEL_UP_CLAIM_ALREADY_CLAIMED'
            ? 'This reward has already been claimed.'
            : 'An error has occurred. Please try again later.';
        this._alert.notifyDanger('Error', message);
      }
      this._logger.error(`Level reward claim failed: ${err}`);
    } finally {
      this.claimingId.set(null);
    }
  }

  close(): void {
    this._dialogRef.close(this._claimedAny);
  }
}
